import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { 
  Package, 
  Clock, 
  CheckCircle2, 
  Truck, 
  Plus, 
  Search, 
  Filter,
  ArrowLeft,
  User,
  ChevronRight,
  XCircle,
  Ban,
  TrendingUp,
  Briefcase,
  ChevronDown,
  ChevronUp,
  Download
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import NotificationBell from "../components/NotificationBell";
import OrderCreationModal from "../components/OrderCreationModal";
import ExportButtons from "../components/ExportButtons";

// Prodotti per macro area (stesso di NewOrder.jsx)
const PRODOTTI_PER_AREA = {
  ORTOPEDIA: [
    { value: "scarpa_ortopedica", label: "Scarpa ortopedica", icon: "👟" },
    { value: "busto", label: "Busto", icon: "🦺" },
    { value: "carrozzina_elettrica", label: "Carrozzina elettrica", icon: "🦽" },
    { value: "apparecchio_acustico", label: "Apparecchio acustico", icon: "🦻" },
    { value: "carrozzina_leggera", label: "Carrozzina leggera", icon: "♿" },
    { value: "seggiolone", label: "Seggiolone", icon: "🪑" },
    { value: "comunicatore", label: "Comunicatore", icon: "📱" },
    { value: "plantare", label: "Plantare", icon: "🦶" },
    { value: "tutore", label: "Tutore", icon: "🩹" },
    { value: "protesi", label: "Protesi", icon: "🦿" },
    { value: "deambulatore", label: "Deambulatore", icon: "🚶" },
    { value: "altro_ortopedia", label: "Altro", icon: "📦" }
  ],
  FINANZIARIO: [
    { value: "prestito_personale", label: "Prestito personale", icon: "💳" },
    { value: "cessione_quinto", label: "Cessione del quinto", icon: "💵" },
    { value: "mutuo", label: "Mutuo", icon: "🏠" },
    { value: "finanziamento", label: "Finanziamento", icon: "💰" },
    { value: "consolidamento_debiti", label: "Consolidamento debiti", icon: "📊" },
    { value: "altro_finanziario", label: "Altro", icon: "📦" }
  ],
  CAF: [
    { value: "dichiarazione_730", label: "Dichiarazione 730", icon: "📄" },
    { value: "isee", label: "ISEE", icon: "📋" },
    { value: "unico", label: "Modello Unico", icon: "📑" },
    { value: "red", label: "Modello RED", icon: "📝" },
    { value: "imu_tasi", label: "IMU/TASI", icon: "🏡" },
    { value: "successione", label: "Successione", icon: "📜" },
    { value: "contratto_locazione", label: "Contratto locazione", icon: "🔑" },
    { value: "altro_caf", label: "Altro", icon: "📦" }
  ],
  PATRONATO: [
    { value: "pensione_vecchiaia", label: "Pensione di vecchiaia", icon: "👴" },
    { value: "pensione_anticipata", label: "Pensione anticipata", icon: "⏰" },
    { value: "pensione_invalidita", label: "Pensione invalidità", icon: "♿" },
    { value: "assegno_sociale", label: "Assegno sociale", icon: "🤝" },
    { value: "naspi", label: "NASpI", icon: "📋" },
    { value: "maternita", label: "Maternità", icon: "👶" },
    { value: "infortunio", label: "Infortunio", icon: "🚑" },
    { value: "malattia", label: "Malattia", icon: "🏥" },
    { value: "reversibilita", label: "Reversibilità", icon: "💫" },
    { value: "altro_patronato", label: "Altro", icon: "📦" }
  ],
  LEGALE: [
    { value: "consulenza_legale", label: "Consulenza legale", icon: "⚖️" },
    { value: "causa_civile", label: "Causa civile", icon: "🏛️" },
    { value: "causa_lavoro", label: "Causa di lavoro", icon: "💼" },
    { value: "recupero_crediti", label: "Recupero crediti", icon: "💸" },
    { value: "separazione_divorzio", label: "Separazione/Divorzio", icon: "💔" },
    { value: "successione_legale", label: "Successione", icon: "📜" },
    { value: "contratti", label: "Contratti", icon: "📝" },
    { value: "altro_legale", label: "Altro", icon: "📦" }
  ],
  ASSICURAZIONE: [
    { value: "rc_auto", label: "RC Auto", icon: "🚗" },
    { value: "polizza_vita", label: "Polizza vita", icon: "❤️" },
    { value: "polizza_casa", label: "Polizza casa", icon: "🏠" },
    { value: "polizza_salute", label: "Polizza salute", icon: "🏥" },
    { value: "polizza_infortuni", label: "Polizza infortuni", icon: "🩹" },
    { value: "sinistro", label: "Gestione sinistro", icon: "📋" },
    { value: "altro_assicurazione", label: "Altro", icon: "📦" }
  ],
  LUCE_E_GAS: [
    { value: "voltura_luce", label: "Voltura luce", icon: "💡" },
    { value: "voltura_gas", label: "Voltura gas", icon: "🔥" },
    { value: "subentro_luce", label: "Subentro luce", icon: "⚡" },
    { value: "subentro_gas", label: "Subentro gas", icon: "🌡️" },
    { value: "nuovo_allaccio_luce", label: "Nuovo allaccio luce", icon: "🔌" },
    { value: "nuovo_allaccio_gas", label: "Nuovo allaccio gas", icon: "🛠️" },
    { value: "cambio_fornitore_luce", label: "Cambio fornitore luce", icon: "🔄" },
    { value: "cambio_fornitore_gas", label: "Cambio fornitore gas", icon: "♻️" },
    { value: "reclamo_bolletta", label: "Reclamo bolletta", icon: "📨" },
    { value: "altro_luce_gas", label: "Altro", icon: "📦" }
  ]
};

const MACRO_AREE_INFO = {
  ORTOPEDIA: { label: "Ortopedia", color: "from-blue-500 to-blue-600", icon: "🦴" },
  FINANZIARIO: { label: "Finanziario", color: "from-green-500 to-green-600", icon: "💰" },
  CAF: { label: "CAF", color: "from-purple-500 to-purple-600", icon: "📋" },
  PATRONATO: { label: "Patronato", color: "from-orange-500 to-orange-600", icon: "🏛️" },
  LEGALE: { label: "Legale", color: "from-red-500 to-red-600", icon: "⚖️" },
  ASSICURAZIONE: { label: "Assicurazione", color: "from-teal-500 to-teal-600", icon: "🛡️" },
  LUCE_E_GAS: { label: "Luce e Gas", color: "from-yellow-500 to-orange-500", icon: "⚡" }
};

const MacroAreaView = ({ user }) => {
  const navigate = useNavigate();
  const { macroArea } = useParams();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ richiesta: 0, pronto: 0, consegnato: 0, non_accolta: 0, respinto: 0, totale: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showPraticheCards, setShowPraticheCards] = useState(true);
  const [praticheStats, setPraticheStats] = useState({});
  
  // Modal state
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedPratica, setSelectedPratica] = useState(null);
  
  const areaInfo = MACRO_AREE_INFO[macroArea] || { label: macroArea, color: "from-slate-500 to-slate-600", icon: "📁" };
  
  useEffect(() => {
    if (!macroArea || !MACRO_AREE_INFO[macroArea]) {
      navigate("/dashboard");
      return;
    }
    fetchData();
  }, [macroArea, searchQuery, statusFilter]);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("macro_area", macroArea);
      if (searchQuery) params.append("search", searchQuery);
      if (statusFilter) params.append("stato", statusFilter);
      
      const [ordersRes, statsRes] = await Promise.all([
        axios.get(`${API}/orders?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/stats?macro_area=${macroArea}`, { withCredentials: true })
      ]);
      
      setOrders(ordersRes.data);
      setStats(statsRes.data);
      
      // Calculate stats per pratica type
      const praticheCount = {};
      ordersRes.data.forEach(order => {
        const pratica = order.prodotto_richiesto || "Altro";
        praticheCount[pratica] = (praticheCount[pratica] || 0) + 1;
      });
      setPraticheStats(praticheCount);
    } catch (error) {
      console.error("Errore caricamento:", error);
      if (error.response?.status === 401) {
        toast.error("Sessione scaduta");
        navigate("/login");
      } else {
        toast.error("Errore nel caricamento dati");
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (stato) => {
    const statusConfig = {
      richiesta: { label: "In richiesta", className: "bg-amber-100 text-amber-700" },
      pronto: { label: "Pronto", className: "bg-blue-100 text-blue-700" },
      consegnato: { label: "Consegnato", className: "bg-green-100 text-green-700" },
      non_accolta: { label: "Non accolta", className: "bg-pink-100 text-pink-700" },
      respinto: { label: "Respinto", className: "bg-red-100 text-red-700" }
    };
    const config = statusConfig[stato] || { label: stato, className: "bg-slate-100 text-slate-700" };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const statsCards = [
    { key: "totale", label: "Totale ordini", value: stats.totale, icon: Package, gradient: "from-slate-800 to-slate-900" },
    { key: "richiesta", label: "In richiesta", value: stats.richiesta, icon: Clock, gradient: "from-amber-400 to-orange-500" },
    { key: "pronto", label: "Pronti", value: stats.pronto, icon: CheckCircle2, gradient: "from-blue-400 to-blue-600" },
    { key: "consegnato", label: "Consegnati", value: stats.consegnato, icon: Truck, gradient: "from-green-400 to-emerald-500" },
    { key: "non_accolta", label: "Non accolta", value: stats.non_accolta, icon: XCircle, gradient: "from-pink-400 to-pink-600" },
    { key: "respinto", label: "Respinti", value: stats.respinto, icon: Ban, gradient: "from-red-400 to-red-600" }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="macro-area-view">
      {/* Modern Header */}
      <header className="sticky top-0 z-50">
        <div className="h-[2px] bg-gradient-to-r from-transparent via-blue-500 to-transparent"></div>
        <div className="backdrop-blur-xl bg-white/70 border-b border-white/20 shadow-lg shadow-slate-900/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate("/dashboard")}
                  className="rounded-xl hover:bg-slate-100/80"
                  data-testid="back-btn"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${areaInfo.color} flex items-center justify-center text-white text-xl shadow-lg`}>
                    {areaInfo.icon}
                  </div>
                  <div>
                    <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {areaInfo.label}
                    </h1>
                    <p className="text-xs text-slate-500 font-medium">Gestione ordini</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <NotificationBell />
                <Button
                  onClick={() => navigate(`/orders/new?area=${macroArea}`)}
                  className="bg-gradient-to-r from-slate-900 to-slate-800 hover:from-slate-800 hover:to-slate-700 shadow-lg hover:shadow-xl transition-all"
                  data-testid="new-order-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuovo Ordine
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {statsCards.map((stat, index) => (
            <div
              key={stat.key}
              className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${stat.gradient} p-4 text-white shadow-lg transform hover:scale-105 transition-transform duration-200 cursor-pointer`}
              style={{ 
                animationDelay: `${index * 100}ms`,
                minHeight: '120px'
              }}
              onClick={() => stat.key !== "totale" && setStatusFilter(statusFilter === stat.key ? "" : stat.key)}
              data-testid={`stat-${stat.key}`}
            >
              <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
              <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/5 rounded-full -ml-8 -mb-8"></div>
              
              <stat.icon className="w-6 h-6 mb-2 opacity-80" />
              <div className="text-3xl font-bold mb-1">{loading ? "-" : stat.value}</div>
              <div className="text-xs opacity-80">{stat.label}</div>
              
              {statusFilter === stat.key && (
                <div className="absolute top-2 right-2 w-2 h-2 bg-white rounded-full animate-pulse"></div>
              )}
            </div>
          ))}
        </div>
        
        {/* Schede Tipo Pratica */}
        <div className="card-hover mb-6 overflow-hidden">
          <button
            onClick={() => setShowPraticheCards(!showPraticheCards)}
            className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
            data-testid="toggle-pratiche-cards"
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${areaInfo.color} flex items-center justify-center`}>
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-slate-900">Tipi di Pratica - {areaInfo.label}</h3>
                <p className="text-xs text-slate-500">{PRODOTTI_PER_AREA[macroArea]?.length || 0} tipologie disponibili</p>
              </div>
            </div>
            {showPraticheCards ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </button>
          
          {showPraticheCards && (
            <div className="p-4 pt-0 border-t border-slate-100">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 mt-4">
                {(PRODOTTI_PER_AREA[macroArea] || []).map((pratica, index) => {
                  const count = praticheStats[pratica.label] || 0;
                  return (
                    <div
                      key={pratica.value}
                      className="group relative p-4 rounded-xl border border-slate-200 bg-white hover:border-blue-400 hover:shadow-lg hover:scale-[1.02] transition-all duration-200 cursor-pointer animate-fade-in"
                      style={{ animationDelay: `${index * 50}ms` }}
                      onClick={() => {
                        setSelectedPratica(pratica);
                        setShowOrderModal(true);
                      }}
                      data-testid={`pratica-card-${pratica.value}`}
                    >
                      {/* Count badge */}
                      {count > 0 && (
                        <div className="absolute -top-2 -right-2 min-w-6 h-6 px-1.5 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-bold flex items-center justify-center shadow-lg">
                          {count}
                        </div>
                      )}
                      
                      {/* Icon */}
                      <div className="text-2xl mb-2 group-hover:scale-110 transition-transform duration-200">
                        {pratica.icon}
                      </div>
                      
                      {/* Label */}
                      <p className="text-xs font-medium text-slate-700 line-clamp-2 leading-tight">
                        {pratica.label}
                      </p>
                      
                      {/* Plus icon on hover */}
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                          <Plus className="w-3 h-3 text-white" />
                        </div>
                      </div>
                      
                      {/* Hover effect */}
                      <div className={`absolute inset-0 rounded-xl bg-gradient-to-br ${areaInfo.color} opacity-0 group-hover:opacity-10 transition-opacity duration-200`}></div>
                    </div>
                  );
                })}
              </div>
              
              {/* Quick action: Create new order with selected pratica */}
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                <p className="text-xs text-slate-500">
                  Clicca su una pratica per creare un nuovo ordine
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchQuery("")}
                  className="text-xs text-slate-500 hover:text-slate-700"
                  disabled={!searchQuery}
                >
                  Rimuovi filtro
                </Button>
              </div>
            </div>
          )}
        </div>
        
        {/* Search and Filters */}
        <div className="card-hover p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca per nome, cognome, CF o prodotto..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Tutti gli stati" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="richiesta">In richiesta</SelectItem>
                <SelectItem value="pronto">Pronto</SelectItem>
                <SelectItem value="consegnato">Consegnato</SelectItem>
                <SelectItem value="non_accolta">Non accolta</SelectItem>
                <SelectItem value="respinto">Respinto</SelectItem>
              </SelectContent>
            </Select>
            <ExportButtons 
              macroArea={macroArea} 
              stato={statusFilter}
            />
          </div>
        </div>
        
        {/* Orders List */}
        <div className="card-hover overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">
              Ordini {areaInfo.label} ({orders.length})
            </h2>
          </div>
          
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
              ))}
            </div>
          ) : orders.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">Nessun ordine in {areaInfo.label}</p>
              <Button
                onClick={() => navigate(`/orders/new?area=${macroArea}`)}
                className="bg-slate-900 hover:bg-slate-800"
              >
                <Plus className="w-4 h-4 mr-2" />
                Crea il primo ordine
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {orders.map((order, index) => (
                <div
                  key={order.order_id}
                  className="p-4 hover:bg-slate-50 transition-colors cursor-pointer flex items-center gap-4 animate-fade-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                  onClick={() => navigate(`/orders/${order.order_id}`)}
                  data-testid={`order-row-${order.order_id}`}
                >
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <User className="w-6 h-6 text-slate-400" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-slate-900">
                        {order.nome} {order.cognome}
                      </span>
                      {getStatusBadge(order.stato)}
                      {order.pagato && (
                        <Badge className="bg-emerald-100 text-emerald-700">Pagato</Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                      <span>{order.order_id}</span>
                      <span>{order.prodotto_richiesto}</span>
                      <span>{formatDate(order.data_richiesta)}</span>
                    </div>
                  </div>
                  
                  <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      
      {/* Order Creation Modal */}
      <OrderCreationModal
        isOpen={showOrderModal}
        onClose={() => {
          setShowOrderModal(false);
          setSelectedPratica(null);
        }}
        macroArea={macroArea}
        selectedPratica={selectedPratica}
        areaInfo={areaInfo}
        onOrderCreated={() => {
          fetchData();
        }}
      />
    </div>
  );
};

export default MacroAreaView;
